/**
 * <jersey-addons> Web Component
 *
 * Renders configurable add-on selectors (patches, version, name+number, shorts, socks)
 * for a jersey product page. Reads JSON config from an inline <script type="application/json">.
 *
 * `option.product` / `group.addon_product` in the config may be either:
 *   - a product handle (string, e.g. "patch-laliga")            ← Recommended
 *   - a variant id (number)
 *   - a Shopify GID (string ending with the variant id digits)
 *
 * Handles are resolved on-connect to their default variant id via /products/<handle>.js
 * and cached in a module-level Map.
 *
 * On add-to-cart:
 *   1. Hidden inputs (properties[...]) are injected into the main product form so the
 *      jersey line item carries the chosen labels (גרסה, פאצים, שם, מספר) +
 *      a per-click `_bundle_id` token.
 *   2. After Horizon's product-form dispatches cart:update (main jersey added),
 *      a second /cart/add.js call adds each selected addon as a separate line item,
 *      tagged with `_bundle_parent` = the same token. Addon quantity is synced from
 *      the parent's final cart quantity (read via /cart.js).
 *   3. A separate cleanup module (loaded by cart-products.liquid) keeps addon
 *      quantities in sync as the parent quantity changes, and removes orphaned
 *      addons when the parent is removed.
 *
 * Config schema: see plans/serene-bubbling-dragon.md.
 */

// Module-level variant-id resolution cache so multiple instances share.
const variantIdCache = new Map();

async function resolveProductRef(ref) {
  if (ref == null || ref === '') return null;

  // Numeric — already a variant id.
  if (typeof ref === 'number' && Number.isFinite(ref)) return ref;

  const str = String(ref).trim();

  // Pure numeric string.
  if (/^\d+$/.test(str)) return parseInt(str, 10);

  // GID (gid://shopify/ProductVariant/12345 or .../Product/12345)
  // — only useful if it's a ProductVariant GID. Product GIDs need a /products/<handle>.js lookup
  // but we don't have a handle from a product GID, so we extract the trailing digits and hope.
  const gidMatch = str.match(/ProductVariant\/(\d+)/);
  if (gidMatch) return parseInt(gidMatch[1], 10);

  // Treat as product handle. Cache lookup.
  const handle = str.toLowerCase();
  if (variantIdCache.has(handle)) return variantIdCache.get(handle);

  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      console.warn(`[jersey-addons] Failed to resolve handle "${handle}": ${res.status}`);
      variantIdCache.set(handle, null);
      return null;
    }
    const data = await res.json();
    const firstVariant = data?.variants?.[0];
    const variantId = firstVariant?.id || null;
    variantIdCache.set(handle, variantId);
    return variantId;
  } catch (err) {
    console.warn(`[jersey-addons] Resolve error for "${handle}":`, err);
    variantIdCache.set(handle, null);
    return null;
  }
}

class JerseyAddons extends HTMLElement {
  constructor() {
    super();
    this.config = [];
    this.selections = {};
    this.basePrice = 0;
    this.currency = 'ILS';
    this.moneyFormat = '₪{{amount}}';
    this.productFormId = '';
    this.currentBundleToken = '';
    this.pendingAddonAdd = false;
    this.boundOnVariantChange = this.onVariantChange.bind(this);
    this.boundCartUpdate = this.onCartUpdate.bind(this);
  }

  async connectedCallback() {
    this.basePrice = parseInt(this.dataset.basePrice || '0', 10);
    this.currency = this.dataset.currency || 'ILS';
    this.moneyFormat = this.dataset.moneyFormat || '₪{{amount}}';
    this.productFormId = this.dataset.productFormId || '';

    const configId = this.dataset.configId;
    const configEl = configId ? document.getElementById(configId) : null;
    if (!configEl) {
      console.warn('[jersey-addons] No config element found:', configId);
      return;
    }

    const rawText = (configEl.textContent || '').trim();
    if (!rawText) {
      console.warn('[jersey-addons] Empty config text. Metafield likely missing or fallback empty.');
      return;
    }

    try {
      let parsed = JSON.parse(rawText);
      // Some Shopify metafield types double-encode JSON as a JSON-string. Detect & re-parse.
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch (e) { /* leave as string */ }
      }
      this.config = Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error('[jersey-addons] Invalid config JSON. Raw text:', rawText.slice(0, 200), err);
      return;
    }

    if (this.config.length === 0) {
      console.warn('[jersey-addons] Config parsed but empty array. Check metafield content.');
      return;
    }

    // Pre-resolve every product handle in the config so add-to-cart is instant.
    await this.preResolveHandles();

    this.render();
    this.attachFormPropertyInjection();
    this.attachCartListener();
    document.addEventListener('variant:change', this.boundOnVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.boundOnVariantChange);
    document.removeEventListener('cart:update', this.boundCartUpdate);
    document
      .querySelectorAll('[id^="jersey-addons-help-"]')
      .forEach((el) => el.remove());
  }

  positionPopover(trigger, popover) {
    const tr = trigger.getBoundingClientRect();
    const pr = popover.getBoundingClientRect();

    // Default: below trigger, right-aligned to trigger's right edge (RTL natural).
    let top = tr.bottom + window.scrollY + 6;
    let left = tr.right + window.scrollX - pr.width;

    // Flip above if no room below.
    if (top + pr.height > window.scrollY + window.innerHeight) {
      top = tr.top + window.scrollY - pr.height - 6;
    }
    // Clamp left edge.
    if (left < window.scrollX + 8) left = window.scrollX + 8;

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
  }

  async preResolveHandles() {
    const refs = new Set();
    this.config.forEach((group) => {
      if (group.addon_product) refs.add(group.addon_product);
      (group.options || []).forEach((opt) => {
        if (opt.product) refs.add(opt.product);
      });
    });
    await Promise.all([...refs].map((ref) => resolveProductRef(ref)));
  }

  onVariantChange(event) {
    if (!event.detail) return;
    const newPrice = event.detail.variant?.price;
    if (typeof newPrice === 'number') {
      this.basePrice = newPrice;
      this.dataset.variantId = event.detail.variant.id;
      this.updateTotal();
    }
  }

  makeBundleToken() {
    return `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /* ---------------------- RENDER ---------------------- */

  render() {
    const groupsEl = this.querySelector('[ref="groups"]');
    if (!groupsEl) return;
    groupsEl.innerHTML = '';

    this.config.forEach((group) => {
      const groupEl = document.createElement('div');
      groupEl.className = 'jersey-addons__group';
      groupEl.dataset.groupKey = group.key;

      const header = document.createElement('div');
      header.className = 'jersey-addons__group-header';
      header.textContent = group.label || group.key;

      if (group.help) {
        const helpId = `jersey-addons-help-${group.key}-${Math.random().toString(36).slice(2, 7)}`;
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'help-tooltip__trigger';
        trigger.setAttribute('aria-label', 'עזרה');
        trigger.setAttribute('popovertarget', helpId);
        trigger.innerHTML = `
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.3"/>
            <circle cx="8" cy="4.5" r="0.95" fill="currentColor"/>
            <rect x="7.25" y="6.5" width="1.5" height="5.2" rx="0.75" fill="currentColor"/>
          </svg>
        `;

        const pop = document.createElement('div');
        pop.id = helpId;
        pop.setAttribute('popover', '');
        pop.className = 'help-tooltip__popover';
        pop.textContent = group.help;

        pop.addEventListener('toggle', (e) => {
          if (e.newState === 'open') this.positionPopover(trigger, pop);
        });

        header.appendChild(trigger);
        document.body.appendChild(pop);
      }
      groupEl.appendChild(header);

      if (group.type === 'radio') this.renderRadioGroup(groupEl, group);
      else if (group.type === 'checkbox') this.renderCheckbox(groupEl, group);
      else if (group.type === 'toggle_with_inputs') this.renderToggleWithInputs(groupEl, group);

      groupsEl.appendChild(groupEl);
    });

    this.updateTotal();
  }

  renderRadioGroup(parent, group) {
    (group.options || []).forEach((option, idx) => {
      const id = `addon-${group.key}-${idx}-${this.dataset.configId}`;
      const label = document.createElement('label');
      label.className = 'jersey-addons__option';
      label.htmlFor = id;

      const text = document.createElement('span');
      text.className = 'jersey-addons__option-label';
      text.textContent = option.label;

      if (option.price_delta_hint && option.price_delta_hint > 0) {
        const price = document.createElement('span');
        price.className = 'jersey-addons__option-price';
        price.textContent = `(+${this.formatMoney(option.price_delta_hint)})`;
        text.appendChild(price);
      }

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `addon-radio-${group.key}-${this.dataset.configId}`;
      input.id = id;
      input.value = option.product || '';
      input.dataset.priceDelta = option.price_delta_hint || 0;
      input.dataset.optionLabel = option.label;
      input.dataset.groupKey = group.key;
      input.dataset.groupLabel = group.label || group.key;
      if (option.default) input.checked = true;

      input.addEventListener('change', () => {
        if (input.checked) {
          this.selections[group.key] = {
            product: option.product,
            label: option.label,
            groupLabel: group.label || group.key,
            priceDelta: parseInt(input.dataset.priceDelta, 10) || 0,
          };
          this.updateTotal();
          this.refreshHiddenInputs();
        }
      });

      if (option.default) {
        this.selections[group.key] = {
          product: option.product,
          label: option.label,
          groupLabel: group.label || group.key,
          priceDelta: parseInt(input.dataset.priceDelta, 10) || 0,
        };
      }

      label.appendChild(text);
      label.appendChild(input);
      parent.appendChild(label);
    });
  }

  renderCheckbox(parent, group) {
    const id = `addon-${group.key}-${this.dataset.configId}`;
    const label = document.createElement('label');
    label.className = 'jersey-addons__option';
    label.htmlFor = id;

    const text = document.createElement('span');
    text.className = 'jersey-addons__option-label';
    text.textContent = group.label;

    if (group.price_delta_hint && group.price_delta_hint > 0) {
      const price = document.createElement('span');
      price.className = 'jersey-addons__option-price';
      price.textContent = `(+${this.formatMoney(group.price_delta_hint)})`;
      text.appendChild(price);
    }

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = id;
    input.dataset.priceDelta = group.price_delta_hint || 0;
    input.dataset.groupKey = group.key;
    input.dataset.groupLabel = group.label || group.key;

    input.addEventListener('change', () => {
      if (input.checked) {
        this.selections[group.key] = {
          product: group.addon_product,
          label: group.label,
          groupLabel: group.label,
          priceDelta: parseInt(input.dataset.priceDelta, 10) || 0,
        };
      } else {
        delete this.selections[group.key];
      }
      this.updateTotal();
      this.refreshHiddenInputs();
    });

    label.appendChild(text);
    label.appendChild(input);
    parent.appendChild(label);
  }

  renderToggleWithInputs(parent, group) {
    const wrapper = document.createElement('div');

    const toggleId = `addon-toggle-${group.key}-${this.dataset.configId}`;
    const toggleLabel = document.createElement('label');
    toggleLabel.className = 'jersey-addons__option';
    toggleLabel.htmlFor = toggleId;

    const toggleText = document.createElement('span');
    toggleText.className = 'jersey-addons__option-label';
    toggleText.textContent = group.label;

    if (group.price_delta_hint && group.price_delta_hint > 0) {
      const price = document.createElement('span');
      price.className = 'jersey-addons__option-price';
      price.textContent = `(+${this.formatMoney(group.price_delta_hint)})`;
      toggleText.appendChild(price);
    }

    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.id = toggleId;
    toggle.dataset.priceDelta = group.price_delta_hint || 0;
    toggle.dataset.groupKey = group.key;

    const inputsContainer = document.createElement('div');
    inputsContainer.className = 'jersey-addons__inputs';
    inputsContainer.dataset.hidden = 'true';

    const inputValues = {};

    (group.inputs || []).forEach((inputDef, idx) => {
      const field = document.createElement('div');
      field.className = 'jersey-addons__input-field';

      const lbl = document.createElement('label');
      const fieldId = `addon-input-${group.key}-${idx}-${this.dataset.configId}`;
      lbl.htmlFor = fieldId;
      lbl.textContent = inputDef.name;

      const inp = document.createElement('input');
      inp.id = fieldId;
      inp.type = inputDef.max ? 'number' : 'text';
      if (inputDef.max_length) inp.maxLength = inputDef.max_length;
      if (typeof inputDef.max === 'number') {
        inp.max = inputDef.max;
        inp.min = 0;
        inp.inputMode = 'numeric';
      }
      inp.placeholder = inputDef.placeholder || '';
      inp.dataset.inputName = inputDef.name;

      inp.addEventListener('input', () => {
        inputValues[inputDef.name] = inp.value.trim();
        if (this.selections[group.key]) {
          this.selections[group.key].inputs = { ...inputValues };
          this.refreshHiddenInputs();
        }
      });

      field.appendChild(lbl);
      field.appendChild(inp);
      inputsContainer.appendChild(field);
    });

    toggle.addEventListener('change', () => {
      if (toggle.checked) {
        inputsContainer.dataset.hidden = 'false';
        this.selections[group.key] = {
          product: group.addon_product,
          label: group.label,
          groupLabel: group.label,
          priceDelta: parseInt(toggle.dataset.priceDelta, 10) || 0,
          inputs: { ...inputValues },
        };
      } else {
        inputsContainer.dataset.hidden = 'true';
        delete this.selections[group.key];
      }
      this.updateTotal();
      this.refreshHiddenInputs();
    });

    toggleLabel.appendChild(toggleText);
    toggleLabel.appendChild(toggle);
    wrapper.appendChild(toggleLabel);
    wrapper.appendChild(inputsContainer);
    parent.appendChild(wrapper);
  }

  /* ---------------------- FORM INTEGRATION ---------------------- */

  attachFormPropertyInjection() {
    if (!this.productFormId) return;
    const form = document.getElementById(this.productFormId);
    if (!form) return;
    this.form = form;
    this.currentBundleToken = this.makeBundleToken();
    this.refreshHiddenInputs();
  }

  refreshHiddenInputs() {
    if (!this.form) return;
    this.form.querySelectorAll('[data-jersey-addons-injected]').forEach((el) => el.remove());

    if (this.currentBundleToken) {
      this.appendHiddenInput('properties[_bundle_id]', this.currentBundleToken);
    }

    Object.values(this.selections).forEach((sel) => {
      if (!sel) return;
      if ((sel.priceDelta > 0 || sel.product) && sel.groupLabel && sel.label) {
        this.appendHiddenInput(`properties[${sel.groupLabel}]`, sel.label);
      }
      if (sel.inputs) {
        Object.entries(sel.inputs).forEach(([n, v]) => {
          if (v) this.appendHiddenInput(`properties[${n}]`, v);
        });
      }
    });
  }

  appendHiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute('data-jersey-addons-injected', '');
    this.form.appendChild(input);
  }

  /* ---------------------- CART UPDATE LISTENER ---------------------- */

  attachCartListener() {
    document.addEventListener('cart:update', this.boundCartUpdate);
  }

  async onCartUpdate(event) {
    const detail = event.detail || {};
    const data = detail.data || {};
    if (data.didError) return;
    if (data.source === 'jersey-addons') return;
    if (data.source !== 'product-form-component') return;
    if (this.pendingAddonAdd) return;

    this.pendingAddonAdd = true;
    const tokenUsed = this.currentBundleToken;

    try {
      const childItems = await this.collectAddonItems();

      // Always rotate token so the next click is a fresh group.
      this.currentBundleToken = this.makeBundleToken();
      this.refreshHiddenInputs();

      if (childItems.length === 0) return;

      // Read parent quantity from the live cart (the source of truth after Horizon's add).
      const parentQuantity = await this.readParentQuantity(tokenUsed);

      const itemsWithQty = childItems.map((it) => ({ ...it, quantity: parentQuantity }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: itemsWithQty, sections: this.getCartSectionIds() }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        console.error('[jersey-addons] Addon add failed:', errBody);
        alert('שגיאה בהוספת תוספות לעגלה: ' + (errBody.description || res.statusText));
        return;
      }
      const parsed = await res.json().catch(() => null);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: parsed,
            sourceId: this.productFormId,
            data: { source: 'jersey-addons', didError: false },
          },
        })
      );
    } catch (err) {
      console.error('[jersey-addons] Addon add error:', err);
    } finally {
      this.pendingAddonAdd = false;
    }
  }

  async collectAddonItems() {
    const items = [];
    const bundleToken = this.currentBundleToken || '';
    for (const sel of Object.values(this.selections)) {
      if (!sel || !sel.product) continue;
      const variantId = await resolveProductRef(sel.product);
      if (!variantId) continue;
      items.push({
        id: variantId,
        quantity: 1,
        properties: {
          _bundle_parent: bundleToken,
          _addon_for_variant: this.dataset.variantId || '',
          _addon_group: sel.groupLabel || '',
        },
      });
    }
    return items;
  }

  async readParentQuantity(token) {
    if (!token) return 1;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return 1;
      const cart = await res.json();
      const match = (cart.items || []).find((i) => {
        const p = i.properties || {};
        return p._bundle_id === token;
      });
      return match?.quantity || 1;
    } catch {
      return 1;
    }
  }

  getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id) ids.push(id);
    });
    return ids.join(',');
  }

  /* ---------------------- TOTAL DISPLAY ---------------------- */

  updateTotal() {
    let total = this.basePrice;
    Object.values(this.selections).forEach((sel) => {
      if (sel && sel.priceDelta) total += sel.priceDelta;
    });
    const out = this.querySelector('[ref="totalAmount"]');
    if (out) out.textContent = this.formatMoney(total);
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, value);
  }
}

if (!customElements.get('jersey-addons')) {
  customElements.define('jersey-addons', JerseyAddons);
}
